import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants, {NOTEBOOK_ITEMS} from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {FolderDefaultsKey} from 'FolderDefaults';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {COMPLEX_POLYMER_DISPLAY_TEXT} from 'modbio/complex-polymers/ComplexPolymerConstants';
import {CreateEntryFromTemplateQuery} from 'notebook/entry-templates/CreateEntryFromTemplateModal/__generated/CreateEntryFromTemplateQuery';
import {SCHEMA_AND_TAGS_FROM_TEMPLATE_QUERY} from 'notebook/entry-templates/CreateEntryFromTemplateModal/CreateEntryFromTemplateModal';
import {clientId} from 'notebook/store/VersioningConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import * as TagUtil from 'schemas/tags/TagUtil';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {typeToName} from 'util/NameUtil';
import * as net from 'util/Net';

import {TemplateCollectionGraphQL} from '../../graphql-fragments/notebook/templates/__generated/TemplateCollectionGraphQL';
import {CloneEntryQuery} from './__generated/CloneEntryQuery';
import BaseCreateModal from './BaseCreateModal';
import {Tag} from './FileConstants';

export const SCHEMA_AND_TAGS_FROM_ENTRY_QUERY: DocumentNode = gql`
  query CloneEntryQuery($id: StrictString!) @skipAudit {
    entry(id: $id) {
      ... on EntryNode {
        id
        tagsJson
        tagSchema {
          ...TagSchemaGraphQL
        }
        template {
          id
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('TagSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const CloneableModelTypes = [
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.ENTRY,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.PROTOCOL,
  ModelTypeConstants.SUBTEMPLATE,
  ModelTypeConstants.TEMPLATE,
] as const;

const CloneableBFIItemTypes = [
  TagSchemaItemType.ANTIBODY,
  TagSchemaItemType.OLIGO_CONJUGATE,
  TagSchemaItemType.OLIGO_DUPLEX,
];

export type CloneableModelType = (typeof CloneableModelTypes)[number];
export type CloneableBFIItemType = (typeof CloneableBFIItemTypes)[number];

interface Props {
  modelType: CloneableModelType;
  itemType?: CloneableBFIItemType;
  id: string;
  versionId: string | number;
  initialTemplateCollection?: TemplateCollectionGraphQL;
  initialFolderId?: string | number;
  initialName?: string;
  onRequestClose: () => void;
}

interface CloneableBFIProps extends Props {
  modelType: ModelTypeConstants.BASIC_FOLDER_ITEM;
  itemType: CloneableBFIItemType;
}

interface CloneableModelProps extends Props {
  modelType: Exclude<CloneableModelType, ModelTypeConstants.BASIC_FOLDER_ITEM>;
  itemType?: never;
}

type CloneModalProps = CloneableBFIProps | CloneableModelProps;

export const getModelTypeRoute = (modelType: CloneableModelType, itemType?: CloneableBFIItemType): string => {
  switch (modelType) {
    case ModelTypeConstants.BASIC_FOLDER_ITEM: {
      switch (itemType) {
        case TagSchemaItemType.OLIGO_CONJUGATE:
        case TagSchemaItemType.OLIGO_DUPLEX: {
          return 'complex-polymers';
        }
        case TagSchemaItemType.ANTIBODY: {
          return 'antibodies';
        }
        default: {
          throw new Error(`Unexpected item type: ${itemType}`);
        }
      }
    }
    default: {
      return pluralize(2, modelType).toLowerCase();
    }
  }
};

const getDisplayText = (modelType: CloneableModelType, itemType?: CloneableBFIItemType): string => {
  if (modelType === ModelTypeConstants.BASIC_FOLDER_ITEM) {
    switch (itemType) {
      case TagSchemaItemType.OLIGO_CONJUGATE:
      case TagSchemaItemType.OLIGO_DUPLEX: {
        return COMPLEX_POLYMER_DISPLAY_TEXT;
      }
      case TagSchemaItemType.ANTIBODY: {
        return 'antibody';
      }
      default: {
        throw new Error(`Unexpected item type: ${itemType}`);
      }
    }
  }
  return typeToName(modelType);
};

function CloneModal(props: CloneModalProps): JSX.Element {
  const result = useSkippableBenchlingQuery<CloneEntryQuery | CreateEntryFromTemplateQuery, {id: string}>(
    props.modelType === ModelTypeConstants.ENTRY
      ? SCHEMA_AND_TAGS_FROM_ENTRY_QUERY
      : SCHEMA_AND_TAGS_FROM_TEMPLATE_QUERY,
    {
      skip:
        (props.modelType !== ModelTypeConstants.ENTRY && props.modelType !== ModelTypeConstants.TEMPLATE) ||
        BenchlingConfig.ELN_ENABLE_SCHEMA_ON_ENTRY_CREATE_MODAL !== true,
      variables: {id: props.id},
      fetchPolicy: 'network-only',
    }
  );

  const data: CloneEntryQuery | CreateEntryFromTemplateQuery | null =
    result.type === QueryResultType.SUCCESS ? result.data : null;

  function handleSubmit(
    name: string,
    folderOrTemplateCollectionId: string,
    tagSchemaId?: string,
    schemaTags?: TagUpdateObject
  ): JQueryPromise<string> {
    const url = `/1/api/${getModelTypeRoute(props.modelType, props.itemType)}/${props.id}/versions/${props.versionId}/clone`;

    if (props.modelType === ModelTypeConstants.BASIC_FOLDER_ITEM) {
      return net
        .postJSON({
          url,
          data: {name, folderId: folderOrTemplateCollectionId},
        })
        .then((resp) => {
          if (props.itemType === TagSchemaItemType.ANTIBODY) {
            return resp.antibodyId;
          } else {
            return resp.complexPolymerId;
          }
        });
    }

    let data;
    if (props.modelType === ModelTypeConstants.TEMPLATE) {
      data = {
        name,
        templateCollectionId: folderOrTemplateCollectionId,
        tagSchemaId,
        schemaTags,
      };
    } else if (props.modelType === ModelTypeConstants.SUBTEMPLATE) {
      data = {
        name,
        templateCollectionId: folderOrTemplateCollectionId,
        clientId,
      };
    } else {
      data = {
        name,
        folder: folderOrTemplateCollectionId,
        tagSchemaId,
        schemaTags,
      };
    }
    return net.postJSON({url, data}).then((resp) => resp.id);
  }

  // Extract schema and tags from query to either template or entry
  function getSchemaAndTagsFromResult(data: CloneEntryQuery | CreateEntryFromTemplateQuery): {
    tags: Array<Tag>;
    tagSchema: TagSchema | null;
  } {
    let tagSchema = null;
    let tags = [];
    if (props.modelType === ModelTypeConstants.ENTRY) {
      const _data = data as CloneEntryQuery;
      tagSchema =
        !isInaccessible(_data.entry) && _data.entry.tagSchema
          ? graphQLToREST('TagSchema', _data.entry.tagSchema)
          : null;
      tags = !isInaccessible(_data.entry) ? _data.entry.tagsJson : [];
    } else {
      const _data = data as CreateEntryFromTemplateQuery;
      tagSchema =
        !isInaccessible(_data.template) && _data.template.tagSchema
          ? graphQLToREST('TagSchema', _data.template.tagSchema)
          : null;
      tags = !isInaccessible(_data.template) ? _data.template.tagsJson : [];
    }
    return {tags, tagSchema};
  }

  // If the schema on an entry originated on a template we don't want users to change it on the modal.
  // This method is not foolproof as it uses the existance of a parent template as a proxy for schema provenance
  // Return undefined if the model is not an entry or a template
  function getEntryProvenance(
    data: CloneEntryQuery | CreateEntryFromTemplateQuery
  ): 'entry' | 'template' | undefined {
    if (props.modelType === ModelTypeConstants.TEMPLATE) {
      return ModelTypeConstants.TEMPLATE;
    } else if (props.modelType === ModelTypeConstants.ENTRY) {
      const _data = data as CloneEntryQuery;
      if (!isInaccessible(_data.entry) && _data.entry.template?.id) {
        return ModelTypeConstants.TEMPLATE;
      }
      return ModelTypeConstants.ENTRY;
    }

    return undefined;
  }

  let folderDefaultsKey: FolderDefaultsKey;
  if (NOTEBOOK_ITEMS.includes(props.modelType)) {
    folderDefaultsKey = FolderDefaultsKey.NOTEBOOK;
  } else {
    folderDefaultsKey = FolderDefaultsKey.INVENTORY;
  }

  // distill schema and schema tags from query response
  const {tags, tagSchema} = data ? getSchemaAndTagsFromResult(data) : {tags: [], tagSchema: null};
  const {schemaTags} = TagUtil.getSortedSchemaAndCustomTags({tags, tagSchema});

  const title = `Clone ${getDisplayText(props.modelType, props.itemType)}`;

  return result.type === QueryResultType.PENDING ? (
    <CenteredHorizontalThrobber />
  ) : (
    <BaseCreateModal
      title={title}
      create={handleSubmit}
      showInventory={false}
      showNotebook={true}
      folderDefaultsKey={folderDefaultsKey}
      modelType={props.modelType}
      initialTemplateCollection={props.initialTemplateCollection}
      initialName={props.initialName}
      onRequestClose={props.onRequestClose}
      initialTagSchema={tagSchema}
      initialSchemaTags={schemaTags}
      entryProvenance={data ? getEntryProvenance(data) : undefined}
      hideStudiesInput={true}
    />
  );
}

export default CloneModal;
